import { ref, readonly } from 'vue'
import type { Pattern } from '@cross-my-heart/shared'

/**
 * IndexedDB-based local storage for offline-first pattern management.
 * Patterns are stored as structured Pattern objects (not raw OXS strings) so
 * sync, conflict resolution, and field-level merging have structured data to
 * work with.
 */

const DB_NAME = 'cross-my-heart'
// Version 5: added patternVersions store for edit history.
const DB_VERSION = 5

// Store names
const PATTERNS_STORE = 'patterns'
const PROJECTS_STORE = 'projects'
const COLLECTIONS_STORE = 'collections'
// Key-value store for app settings (e.g. seeded flag). Not synced to server.
const SETTINGS_STORE = 'settings'
// Append-only log of pattern snapshots; each save creates a new entry.
const PATTERN_VERSIONS_STORE = 'patternVersions'

/**
 * A pattern stored locally.
 * `dirty` is true when there are local changes not yet synced to the server.
 * `serverId` is the server-assigned ID stored after a successful sync.
 * `serverUpdatedAt` is the server's `updated_at` timestamp from the last successful sync,
 *   used to detect conflicts: if the server's version is newer, it wins (last-write-wins).
 * `tags` and `notes` are user-authored metadata added after import.
 * `forkedFromId` points to the local pattern this was derived from (fork lineage).
 */
export interface LocalPattern {
  id: string
  serverId?: string
  title: string
  pattern: Pattern
  dirty: boolean
  createdAt: string
  tags?: string[]
  notes?: string
  /** Per-thread symbol overrides for the viewer. Key is threadIndex.toString(). Not synced — display-only. */
  symbolOverrides?: Record<string, string>
  /** Server's `updated_at` at the time of last successful sync. Null until first push succeeds. */
  serverUpdatedAt?: string | null
  /** Local ID of the pattern this was forked from, if any. */
  forkedFromId?: string
  /** Number of times this pattern has been edited and saved (1-indexed). */
  currentVersion?: number
  /**
   * Stable id of the LocalPatternVersion that is the live pattern. Used to mark the
   * "Current" entry in version history. `versionNumber` alone is ambiguous — a
   * sync-conflict snapshot is recorded at the live `currentVersion` without advancing
   * it (see `saveVersionSnapshot`), so it can collide with the live entry's number
   * (#147). Set only by `savePatternWithVersion`; unset for patterns whose live
   * content arrived via sync, in which case nothing is marked "Current" until the
   * next versioned save.
   */
  currentVersionId?: string
}

/**
 * A snapshot of a pattern at a specific edit version.
 * Created whenever the pattern editor saves changes.
 */
export interface LocalPatternVersion {
  id: string
  patternId: string
  versionNumber: number
  snapshot: Pattern
  changeSummary: string
  createdAt: string
}

/**
 * A named group of patterns. Local-only for now — server sync follows once
 * field-level sync is in place. Deleting a collection does not delete its patterns.
 */
export interface Collection {
  id: string
  name: string
  patternIds: string[]
  createdAt: string
}

/**
 * A stitch completion record.
 *
 * `threadIndex` is the palette thread the cell was actually stitched with under
 * the paint model (#189): the charted thread for an Auto-pen stitch, or a
 * deviating thread when stitched with a specific pen. Optional for backward
 * compatibility — completions recorded before the paint model carry no index and
 * resolve to the cell's charted thread.
 */
export interface StitchCompletion {
  x: number
  y: number
  completedAt: number
  threadIndex?: number
}

/**
 * A project (instance of working on a pattern).
 * `title` defaults to the source pattern's title but can be renamed.
 * `patternSnapshot` is a deep copy of the source pattern's content captured at
 * creation. A project renders from this snapshot, NOT the live pattern, so later
 * edits to the source blueprint never leak into in-progress work (#124). It is
 * local/display-only — like `symbolOverrides`, it is not part of the sync payload,
 * so a project pulled on another device falls back to that device's live pattern.
 * Optional for backward compatibility with projects created before snapshots existed.
 * `viewState` persists the workspace's pan/zoom (a Zoompinch transform: x/y in
 * wrapper px, scale where 1 == fit-to-viewbox, rotate) so it restores to the last
 * view on re-open (#125). Older projects may carry the pre-#125 scroll/zoom shape;
 * the workspace ignores anything without a `scale` and falls back to fit.
 * Project-level deviations from the chart are recorded directly on each
 * completion's `threadIndex` via the paint-model pen (#189/#190); the pattern
 * blueprint is unchanged. (The earlier global `threadSubstitutions` recolour and
 * per-stitch "happy accident" overrides were both removed — the pen is the single
 * deviation mechanism.)
 * `photoUrls` are R2-backed URLs of progress/finished photos attached to this project.
 * URLs are stored locally; the R2 object is the source of truth for the actual image.
 */
export interface Project {
  id: string
  patternId: string
  title: string
  patternSnapshot?: Pattern
  completions: StitchCompletion[]
  createdAt: string
  updatedAt: string
  viewState?: { x: number; y: number; scale: number; rotate: number }
  photoUrls?: string[]
  // ─── Sync (#62) — the project id doubles as its server id ───
  /** True when there are local changes not yet pushed to the server. */
  dirty?: boolean
  /** Server `updated_at` of the last successful sync; drives If-Unmodified-Since + pull staleness. */
  serverUpdatedAt?: string | null
}

// Singleton database connection
let db: IDBDatabase | null = null
const isReady = ref(false)
const error = ref<string | null>(null)

/**
 * Initialize the IndexedDB database
 */
async function initDB(): Promise<IDBDatabase> {
  if (db) return db

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = (event) => {
      const target = event.target as IDBOpenDBRequest
      const errorMsg = target.error?.message || 'Unknown error'
      error.value = `Failed to open local database: ${errorMsg}`
      reject(new Error(`Failed to open IndexedDB: ${errorMsg}`))
    }

    request.onblocked = () => {
      error.value = 'Database is blocked. Please close other tabs using this app.'
      reject(new Error('IndexedDB blocked'))
    }

    request.onsuccess = () => {
      db = request.result
      isReady.value = true
      resolve(db)
    }

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result
      const oldVersion = event.oldVersion

      if (oldVersion < 1) {
        database.createObjectStore(PATTERNS_STORE, { keyPath: 'id' })
        const projectsStore = database.createObjectStore(PROJECTS_STORE, { keyPath: 'id' })
        projectsStore.createIndex('patternId', 'patternId', { unique: false })
      }

      if (oldVersion < 2) {
        // Patterns now hold structured Pattern objects instead of OXS strings.
        // OXS-string data can't be migrated without the parser at upgrade time; clear and restart.
        if (database.objectStoreNames.contains(PATTERNS_STORE)) {
          database.deleteObjectStore(PATTERNS_STORE)
        }
        database.createObjectStore(PATTERNS_STORE, { keyPath: 'id' })
      }

      if (oldVersion < 3) {
        database.createObjectStore(COLLECTIONS_STORE, { keyPath: 'id' })
      }

      if (oldVersion < 4) {
        database.createObjectStore(SETTINGS_STORE, { keyPath: 'key' })
      }

      if (oldVersion < 5) {
        const vs = database.createObjectStore(PATTERN_VERSIONS_STORE, { keyPath: 'id' })
        vs.createIndex('patternId', 'patternId', { unique: false })
      }
    }
  })
}

/**
 * Get a database transaction
 */
async function getTransaction(
  storeNames: string | string[],
  mode: IDBTransactionMode = 'readonly'
): Promise<IDBTransaction> {
  const database = await initDB()
  return database.transaction(storeNames, mode)
}

/**
 * Build a Set for O(1) completion lookups
 */
export function buildCompletionSet(completions: StitchCompletion[]): Set<string> {
  return new Set(completions.map((c) => `${c.x}_${c.y}`))
}

/**
 * Build a `"x_y"`-keyed Map for O(1) lookup of the completion *record* at a cell
 * (not just membership). Workspace paints once per stitch on every repaint; with a
 * plain array, each lookup linear-scanned all completions, so a large near-complete
 * chart was O(stitches × completions) per paint (#198). On a key collision the last
 * record wins — matching `setCell`, which keeps a single completion per cell.
 */
export function buildCompletionMap(completions: StitchCompletion[]): Map<string, StitchCompletion> {
  return new Map(completions.map((c) => [`${c.x}_${c.y}`, c]))
}

/**
 * Composable for local pattern and project storage
 */
export function useLocalStore() {
  // ============================================
  // Pattern operations
  // ============================================

  /**
   * Get all patterns from local storage
   */
  async function getAllPatterns(): Promise<LocalPattern[]> {
    const tx = await getTransaction(PATTERNS_STORE)
    const store = tx.objectStore(PATTERNS_STORE)

    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(new Error('Failed to get patterns'))
    })
  }

  /**
   * Get a single pattern by ID
   */
  async function getPattern(id: string): Promise<LocalPattern | undefined> {
    const tx = await getTransaction(PATTERNS_STORE)
    const store = tx.objectStore(PATTERNS_STORE)

    return new Promise((resolve, reject) => {
      const request = store.get(id)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(new Error('Failed to get pattern'))
    })
  }

  /**
   * Save a pattern to local storage
   */
  async function savePattern(pattern: LocalPattern): Promise<void> {
    const tx = await getTransaction(PATTERNS_STORE, 'readwrite')
    const idbStore = tx.objectStore(PATTERNS_STORE)

    return new Promise((resolve, reject) => {
      // Resolve on tx.oncomplete (durable commit), not request.onsuccess — the put
      // request succeeds before the transaction commits; a quota abort fires after
      // onsuccess and would silently swallow the error if we resolved there.
      idbStore.put(pattern)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('Failed to save pattern'))
      tx.onabort = () => reject(tx.error ?? new Error('Pattern save aborted'))
    })
  }

  /**
   * Delete a pattern from local storage
   */
  async function deletePattern(id: string): Promise<void> {
    const tx = await getTransaction(PATTERNS_STORE, 'readwrite')
    const store = tx.objectStore(PATTERNS_STORE)

    return new Promise((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to delete pattern'))
    })
  }

  /**
   * Create a new pattern from a parsed Pattern object. Marked dirty immediately
   * so it will be picked up by the next sync.
   */
  async function createPattern(title: string, pattern: Omit<Pattern, 'id'>): Promise<LocalPattern> {
    const id = crypto.randomUUID()
    const localPattern: LocalPattern = {
      id,
      title,
      // Align Pattern.id with LocalPattern.id so both halves share a single canonical ID.
      pattern: { ...pattern, id },
      dirty: true,
      createdAt: new Date().toISOString(),
    }

    await savePattern(localPattern)
    return localPattern
  }

  /** Returns all patterns with dirty: true that need to be synced to the server. */
  async function getDirtyPatterns(): Promise<LocalPattern[]> {
    const all = await getAllPatterns()
    return all.filter((p) => p.dirty)
  }

  /**
   * Mark a pattern as synced: clears dirty flag, records the server-assigned ID,
   * and stores the server's `updated_at` timestamp for future conflict detection.
   * The local id is preserved — it's the stable reference used by projects.
   */
  async function markSynced(
    localId: string,
    serverId: string,
    serverUpdatedAt?: string | null
  ): Promise<void> {
    const pattern = await getPattern(localId)
    if (!pattern) return
    await savePattern({
      ...pattern,
      dirty: false,
      serverId,
      serverUpdatedAt: serverUpdatedAt ?? null,
    })
  }

  /**
   * Update user-authored metadata (tags, notes) on a pattern.
   * Marks the pattern dirty so it will be picked up by the next sync.
   */
  async function updatePatternMeta(
    id: string,
    updates: Partial<Pick<LocalPattern, 'tags' | 'notes'>>
  ): Promise<void> {
    const pattern = await getPattern(id)
    if (!pattern) return
    await savePattern({ ...pattern, ...updates, dirty: true })
  }

  /**
   * Persist per-thread symbol overrides for the pattern viewer.
   * These are display-only preferences — not synced to the server (no dirty flag).
   */
  async function updatePatternSymbols(
    id: string,
    symbolOverrides: Record<string, string>
  ): Promise<void> {
    const pattern = await getPattern(id)
    if (!pattern) return
    await savePattern({ ...pattern, symbolOverrides })
  }

  /**
   * Save an edited pattern with a new version snapshot.
   * Atomically writes the updated LocalPattern and a new LocalPatternVersion entry.
   */
  async function savePatternWithVersion(
    localPattern: LocalPattern,
    updatedData: Pattern,
    changeSummary: string
  ): Promise<void> {
    // Seed a baseline version the first time a pattern is versioned. Patterns are
    // created without any version entry, so without this the original (pre-edit)
    // content is never recorded: the first edit overwrites it with no way back, and
    // the only entry in history would be identical to the live pattern — making
    // "Restore" appear to do nothing (#123). The baseline captures the *outgoing*
    // state at its current version number, distinct from the new entry below.
    const existingVersions = await getPatternVersions(localPattern.id)
    const baselineEntry: LocalPatternVersion | null =
      existingVersions.length === 0
        ? {
            id: crypto.randomUUID(),
            patternId: localPattern.id,
            versionNumber: localPattern.currentVersion ?? 0,
            // JSON round-trip strips Vue reactive proxies — when called from the
            // editor `localPattern` is reactive, and IDB's structured clone rejects
            // the deep proxy. `updatedData` below is already plain (the caller
            // round-trips it), so only the baseline needs cloning here.
            snapshot: JSON.parse(JSON.stringify(localPattern.pattern)) as Pattern,
            changeSummary: 'Original (before first edit)',
            createdAt: new Date().toISOString(),
          }
        : null

    const nextVersion = (localPattern.currentVersion ?? 0) + 1
    const versionEntry: LocalPatternVersion = {
      id: crypto.randomUUID(),
      patternId: localPattern.id,
      versionNumber: nextVersion,
      snapshot: updatedData,
      changeSummary,
      createdAt: new Date().toISOString(),
    }
    const updated: LocalPattern = {
      ...localPattern,
      pattern: updatedData,
      title: updatedData.info.title,
      currentVersion: nextVersion,
      // Track the live version by stable id, not number, so a sync-conflict snapshot
      // sharing this number isn't mistaken for the current version in history (#147).
      currentVersionId: versionEntry.id,
      dirty: true,
    }
    const database = await initDB()
    await new Promise<void>((resolve, reject) => {
      const tx = database.transaction([PATTERNS_STORE, PATTERN_VERSIONS_STORE], 'readwrite')
      tx.objectStore(PATTERNS_STORE).put(updated)
      const versionsStore = tx.objectStore(PATTERN_VERSIONS_STORE)
      if (baselineEntry) versionsStore.put(baselineEntry)
      versionsStore.put(versionEntry)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })
  }

  /**
   * Save a read-only snapshot of the current pattern state as a version entry.
   * Does NOT modify the pattern record — used as a best-effort safety net before
   * destructive operations (e.g. conflict overwrites during sync).
   */
  async function saveVersionSnapshot(
    localPattern: LocalPattern,
    changeSummary: string
  ): Promise<void> {
    const versionEntry: LocalPatternVersion = {
      id: crypto.randomUUID(),
      patternId: localPattern.id,
      // Snapshot records the state *at* the current version, not an advancement of it.
      // Using currentVersion (not +1) keeps snapshot numbering distinct from the next editor save.
      versionNumber: localPattern.currentVersion ?? 0,
      snapshot: localPattern.pattern,
      changeSummary,
      createdAt: new Date().toISOString(),
    }
    const database = await initDB()
    await new Promise<void>((resolve, reject) => {
      const tx = database.transaction(PATTERN_VERSIONS_STORE, 'readwrite')
      tx.objectStore(PATTERN_VERSIONS_STORE).put(versionEntry)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })
  }

  /** Return all saved versions for a pattern, ordered by versionNumber ascending. */
  async function getPatternVersions(patternId: string): Promise<LocalPatternVersion[]> {
    const database = await initDB()
    return new Promise((resolve, reject) => {
      const tx = database.transaction(PATTERN_VERSIONS_STORE, 'readonly')
      const index = tx.objectStore(PATTERN_VERSIONS_STORE).index('patternId')
      const req = index.getAll(patternId)
      req.onsuccess = () =>
        resolve(
          (req.result as LocalPatternVersion[]).sort((a, b) => a.versionNumber - b.versionNumber)
        )
      req.onerror = () => reject(req.error)
    })
  }

  /**
   * Restore a pattern to a previously saved version.
   *
   * Non-destructive: the current state is first snapshotted (so a restore can
   * itself be undone), then the chosen version's snapshot is written back as a
   * new dirty version — giving a clear audit trail and letting the restored
   * state sync to the server like any other edit.
   *
   * Returns the restored LocalPattern, or null if the pattern or version is
   * missing (e.g. already deleted).
   */
  async function restoreVersion(
    patternId: string,
    versionId: string
  ): Promise<LocalPattern | null> {
    const current = await getPattern(patternId)
    if (!current) return null
    const versions = await getPatternVersions(patternId)
    const target = versions.find((v) => v.id === versionId)
    if (!target) return null

    await saveVersionSnapshot(
      current,
      `Auto-saved before restoring version ${target.versionNumber}`
    )
    await savePatternWithVersion(
      current,
      target.snapshot,
      `Restored from version ${target.versionNumber}`
    )
    return (await getPattern(patternId)) ?? null
  }

  // ============================================
  // Project operations
  // ============================================

  /**
   * Get all projects from local storage
   */
  async function getAllProjects(): Promise<Project[]> {
    const tx = await getTransaction(PROJECTS_STORE)
    const store = tx.objectStore(PROJECTS_STORE)

    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(new Error('Failed to get projects'))
    })
  }

  /**
   * Get a single project by ID
   */
  async function getProject(id: string): Promise<Project | undefined> {
    const tx = await getTransaction(PROJECTS_STORE)
    const store = tx.objectStore(PROJECTS_STORE)

    return new Promise((resolve, reject) => {
      const request = store.get(id)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(new Error('Failed to get project'))
    })
  }

  /**
   * Get all projects for a specific pattern
   */
  async function getProjectsForPattern(patternId: string): Promise<Project[]> {
    const tx = await getTransaction(PROJECTS_STORE)
    const store = tx.objectStore(PROJECTS_STORE)
    const index = store.index('patternId')

    return new Promise((resolve, reject) => {
      const request = index.getAll(IDBKeyRange.only(patternId))
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(new Error('Failed to get projects for pattern'))
    })
  }

  /** Low-level project writer — persists the record exactly as given. */
  async function putProjectRaw(project: Project): Promise<void> {
    const tx = await getTransaction(PROJECTS_STORE, 'readwrite')
    const store = tx.objectStore(PROJECTS_STORE)

    return new Promise((resolve, reject) => {
      // Set up transaction handlers BEFORE making the request
      // to ensure they're in place before auto-commit
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('Failed to save project - transaction error'))
      // A quota-triggered abort would otherwise leave this promise unresolved (matches savePattern).
      tx.onabort = () => reject(tx.error ?? new Error('Project save aborted'))

      const request = store.put(project)
      request.onerror = () => reject(new Error('Failed to save project'))
    })
  }

  /**
   * Save a project after a local edit: bumps `updatedAt` and marks it `dirty`
   * so the next sync pushes it. (Sync-originated writes use the raw/synced
   * helpers below so they don't re-dirty the record.)
   */
  async function saveProject(project: Project): Promise<void> {
    await putProjectRaw({ ...project, updatedAt: new Date().toISOString(), dirty: true })
  }

  /** Projects with local changes not yet pushed to the server. */
  async function getDirtyProjects(): Promise<Project[]> {
    const all = await getAllProjects()
    return all.filter((p) => p.dirty)
  }

  /**
   * Mark a project as synced: clear `dirty` and record the server timestamp,
   * without bumping `updatedAt` (which would re-dirty it on the next pull check).
   */
  async function markProjectSynced(id: string, serverUpdatedAt: string | null): Promise<void> {
    const project = await getProject(id)
    if (!project) return
    await putProjectRaw({ ...project, dirty: false, serverUpdatedAt })
  }

  /**
   * Write a server-sourced project into the local store (pull). Clean by
   * definition; its `updatedAt` comes from the server so list ordering matches.
   */
  async function applyServerProject(project: Project): Promise<void> {
    await putProjectRaw({ ...project, dirty: false })
  }

  /**
   * Delete a project from local storage
   */
  async function deleteProject(id: string): Promise<void> {
    const tx = await getTransaction(PROJECTS_STORE, 'readwrite')
    const store = tx.objectStore(PROJECTS_STORE)

    return new Promise((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to delete project'))
    })
  }

  /**
   * Create a new project for a pattern. Pass the pattern title so the project
   * has a human-readable name from the start (can be renamed later).
   *
   * Snapshots the pattern's content into the project so later edits to the source
   * blueprint never leak into this in-progress work (#124). `patternId` is retained
   * for lineage/navigation, but the workspace renders from the snapshot.
   */
  async function createProject(patternId: string, title: string): Promise<Project> {
    const now = new Date().toISOString()
    const source = await getPattern(patternId)
    const project: Project = {
      id: crypto.randomUUID(),
      patternId,
      title,
      patternSnapshot: source ? structuredClone(source.pattern) : undefined,
      completions: [],
      createdAt: now,
      updatedAt: now,
    }

    await saveProject(project)
    return project
  }

  /**
   * Delete all projects for a pattern
   */
  async function deleteProjectsForPattern(patternId: string): Promise<void> {
    const projects = await getProjectsForPattern(patternId)
    for (const project of projects) {
      await deleteProject(project.id)
    }
  }

  // ============================================
  // Collection operations
  // ============================================

  async function getAllCollections(): Promise<Collection[]> {
    const tx = await getTransaction(COLLECTIONS_STORE)
    const store = tx.objectStore(COLLECTIONS_STORE)
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(new Error('Failed to get collections'))
    })
  }

  async function getCollection(id: string): Promise<Collection | undefined> {
    const tx = await getTransaction(COLLECTIONS_STORE)
    const store = tx.objectStore(COLLECTIONS_STORE)
    return new Promise((resolve, reject) => {
      const request = store.get(id)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(new Error('Failed to get collection'))
    })
  }

  async function saveCollection(collection: Collection): Promise<void> {
    const tx = await getTransaction(COLLECTIONS_STORE, 'readwrite')
    const store = tx.objectStore(COLLECTIONS_STORE)
    return new Promise((resolve, reject) => {
      const request = store.put(collection)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to save collection'))
    })
  }

  /** Create a new empty collection. */
  async function createCollection(name: string): Promise<Collection> {
    const collection: Collection = {
      id: crypto.randomUUID(),
      name: name.trim(),
      patternIds: [],
      createdAt: new Date().toISOString(),
    }
    await saveCollection(collection)
    return collection
  }

  /** Rename a collection. */
  async function renameCollection(id: string, name: string): Promise<void> {
    const collection = await getCollection(id)
    if (!collection) return
    await saveCollection({ ...collection, name: name.trim() })
  }

  /**
   * Delete a collection. Does not delete the patterns inside it —
   * collections are just labels.
   */
  async function deleteCollection(id: string): Promise<void> {
    const tx = await getTransaction(COLLECTIONS_STORE, 'readwrite')
    const store = tx.objectStore(COLLECTIONS_STORE)
    return new Promise((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to delete collection'))
    })
  }

  async function addPatternToCollection(collectionId: string, patternId: string): Promise<void> {
    const collection = await getCollection(collectionId)
    if (!collection || collection.patternIds.includes(patternId)) return
    await saveCollection({ ...collection, patternIds: [...collection.patternIds, patternId] })
  }

  async function removePatternFromCollection(
    collectionId: string,
    patternId: string
  ): Promise<void> {
    const collection = await getCollection(collectionId)
    if (!collection) return
    await saveCollection({
      ...collection,
      patternIds: collection.patternIds.filter((id) => id !== patternId),
    })
  }

  /** Remove a pattern from every collection it belongs to. Call before deleting a pattern. */
  async function removePatternFromAllCollections(patternId: string): Promise<void> {
    const all = await getAllCollections()
    for (const c of all) {
      if (c.patternIds.includes(patternId)) {
        await saveCollection({ ...c, patternIds: c.patternIds.filter((id) => id !== patternId) })
      }
    }
  }

  // ============================================
  // Settings operations
  // ============================================

  async function getSetting(key: string): Promise<unknown> {
    const database = await initDB()
    return new Promise((resolve, reject) => {
      const tx = database.transaction(SETTINGS_STORE, 'readonly')
      const req = tx.objectStore(SETTINGS_STORE).get(key)
      req.onsuccess = () => {
        const record = req.result as { key: string; value: unknown } | undefined
        resolve(record?.value)
      }
      req.onerror = () => reject(req.error)
    })
  }

  async function setSetting(key: string, value: unknown): Promise<void> {
    const database = await initDB()
    return new Promise((resolve, reject) => {
      const tx = database.transaction(SETTINGS_STORE, 'readwrite')
      const req = tx.objectStore(SETTINGS_STORE).put({ key, value })
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })
  }

  /**
   * Seed the library with the bundled Cross Stitch Club logo pattern on first launch.
   *
   * The `seeded` flag in the settings store prevents re-seeding on subsequent
   * launches. Deleting the seeded pattern does not cause a re-seed — the flag
   * stays set so a user can clean their own library without the app fighting back.
   *
   * The pattern write and the seeded flag are committed in a single two-store
   * transaction so a mid-write crash cannot leave them out of sync (which would
   * cause a duplicate seed on the next launch).
   */
  async function seedLibraryIfNeeded(): Promise<void> {
    const alreadySeeded = await getSetting('seeded')
    if (alreadySeeded) return

    const { SEED_PATTERN } = await import('../assets/seed-pattern.js')
    // Seed patterns preserve the original pattern.id from the curated asset so that
    // BrowsePatterns can detect "already in library" by matching lp.pattern.id.
    // The localPattern.id (storage key) intentionally differs from pattern.id here.
    const localPattern: LocalPattern = {
      id: crypto.randomUUID(),
      title: SEED_PATTERN.info.title,
      pattern: SEED_PATTERN,
      dirty: false,
      createdAt: new Date().toISOString(),
    }

    const database = await initDB()
    await new Promise<void>((resolve, reject) => {
      const tx = database.transaction([PATTERNS_STORE, SETTINGS_STORE], 'readwrite')
      tx.objectStore(PATTERNS_STORE).put(localPattern)
      tx.objectStore(SETTINGS_STORE).put({ key: 'seeded', value: true })
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })
  }

  return {
    // State
    isReady: readonly(isReady),
    error: readonly(error),

    // Initialize
    initDB,

    // Pattern operations
    getAllPatterns,
    getPattern,
    savePattern,
    deletePattern,
    createPattern,
    getDirtyPatterns,
    markSynced,
    updatePatternMeta,
    updatePatternSymbols,
    savePatternWithVersion,
    saveVersionSnapshot,
    getPatternVersions,
    restoreVersion,

    // Project operations
    getAllProjects,
    getProject,
    getProjectsForPattern,
    saveProject,
    deleteProject,
    createProject,
    deleteProjectsForPattern,
    getDirtyProjects,
    markProjectSynced,
    applyServerProject,

    // Collection operations
    getAllCollections,
    getCollection,
    saveCollection,
    createCollection,
    renameCollection,
    deleteCollection,
    addPatternToCollection,
    removePatternFromCollection,
    removePatternFromAllCollections,

    // Settings / first-run
    getSetting,
    setSetting,
    seedLibraryIfNeeded,

    // Helpers
    buildCompletionSet,
    buildCompletionMap,
  }
}
